import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Search, Filter, X } from 'lucide-react';

/**
 * DataTable search + filter chrome (iter276 extraction).
 * Renders the top search bar, the Filters toggle button, the Clear button,
 * and (when expanded) the per-column filter panel.
 */
const DataTableFilters = ({
  searchable,
  searchTerm,
  setSearchTerm,
  filterableColumns,
  filters,
  setFilters,
  showFilters,
  setShowFilters,
  hasActiveFilters,
  clearFilters,
  activeFilterCount,
  testIdPrefix,
}) => {
  if (!searchable && filterableColumns.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap gap-3 items-center">
        {searchable && (
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
        )}

        {filterableColumns.length > 0 && (
          <Button
            variant={showFilters ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            data-testid={`${testIdPrefix}-filter-toggle`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 bg-blue-500 text-white text-xs rounded-full px-2">
                {activeFilterCount}
              </span>
            )}
          </Button>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-slate-500"
            data-testid={`${testIdPrefix}-clear-filters`}
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {showFilters && filterableColumns.length > 0 && (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {filterableColumns.map(col => (
              <div key={col.key} className="space-y-1">
                <label className="text-sm font-medium text-slate-700">{col.label}</label>
                {col.filterType === 'select' && col.filterOptions ? (
                  <Select
                    value={filters[col.key] || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, [col.key]: value }))}
                  >
                    <SelectTrigger data-testid={`${testIdPrefix}-filter-${col.key}`}>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {col.filterOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder={`Filter by ${col.label.toLowerCase()}`}
                    value={filters[col.key] || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                    data-testid={`${testIdPrefix}-filter-${col.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default DataTableFilters;
